/**
 * Pure field-array editing helpers for the Pass Designer Fields section. All
 * functions are immutable — they return a new PassDesign, never mutating the
 * input — so React state updates stay referentially safe.
 *
 * Group invariant: an empty group is stored OMITTED (the key is deleted), not as
 * `[]`, so the wallet-apple builder's "omit when empty" contract and the
 * mergePassDesign array-replace semantics stay clean.
 *
 * exactOptionalPropertyTypes: we never write explicit `undefined` — clearing an
 * optional prop deletes the key.
 */

import type { PassDesign, PassDesignField } from '@insurwiz/wallet-core';

import { dedupeKeyInGroup, slugifyKey } from './fieldLabels';

/** The five field groups, in render order (mirrors FIELD_GROUPS in validatePassDesign). */
export const FIELD_GROUPS = ['header', 'primary', 'secondary', 'auxiliary', 'back'] as const;
export type FieldGroup = (typeof FIELD_GROUPS)[number];

/** Optional, non-mutually-exclusive string props the row editor sets directly. */
export type FieldStringProp =
  | 'key'
  | 'label'
  | 'value'
  | 'changeMessage'
  | 'textAlignment'
  | 'dateStyle'
  | 'timeStyle'
  | 'currencyCode'
  | 'numberStyle';

function readGroup(design: PassDesign, group: FieldGroup): PassDesignField[] {
  return design.fields[group] ?? [];
}

/** Replace one group's array; an empty array OMITS the key. */
export function setGroup(
  design: PassDesign,
  group: FieldGroup,
  rows: PassDesignField[],
): PassDesign {
  const fields = { ...design.fields };
  if (rows.length === 0) {
    delete fields[group];
  } else {
    fields[group] = rows;
  }
  return { ...design, fields };
}

/** Append a fresh empty row to a group. */
export function addField(design: PassDesign, group: FieldGroup): PassDesign {
  const rows = [...readGroup(design, group), { key: '', value: '' } satisfies PassDesignField];
  return setGroup(design, group, rows);
}

/** Splice a row out (rewrites the whole array; omits the group if it empties). */
export function removeField(design: PassDesign, group: FieldGroup, index: number): PassDesign {
  const rows = readGroup(design, group).filter((_, i) => i !== index);
  return setGroup(design, group, rows);
}

/** Move a row by `delta` (-1 up, +1 down), clamped to the group bounds. */
export function moveField(
  design: PassDesign,
  group: FieldGroup,
  index: number,
  delta: number,
): PassDesign {
  const rows = [...readGroup(design, group)];
  const target = index + delta;
  if (index < 0 || index >= rows.length || target < 0 || target >= rows.length) return design;
  const moved = rows[index];
  const displaced = rows[target];
  if (moved === undefined || displaced === undefined) return design;
  rows[index] = displaced;
  rows[target] = moved;
  return setGroup(design, group, rows);
}

/**
 * Set (or clear) one string prop on a row. An empty string or `undefined`
 * DELETES the key (never writes explicit undefined). Setting `currencyCode`
 * clears `numberStyle` and vice-versa (the mutual-exclusion rule).
 */
export function setFieldProp(
  design: PassDesign,
  group: FieldGroup,
  index: number,
  prop: FieldStringProp,
  value: string | undefined,
): PassDesign {
  const rows = readGroup(design, group);
  const current = rows[index];
  if (current === undefined) return design;

  const next: PassDesignField = { ...current };
  if (value === undefined || value === '') {
    delete next[prop];
  } else {
    // All FieldStringProp values are string-typed at runtime (the literal-union
    // props included); writing through a string-record view avoids the
    // union-keyed `never` write target while preserving the exact runtime value.
    (next as Record<FieldStringProp, string>)[prop] = value;
  }
  if (prop === 'currencyCode' && value !== undefined && value !== '') delete next.numberStyle;
  if (prop === 'numberStyle' && value !== undefined && value !== '') delete next.currencyCode;

  const updated = rows.map((row, i) => (i === index ? next : row));
  return setGroup(design, group, updated);
}

/** The `(group)` key set that drives [LIVE]: a row is [LIVE] iff its `key`
 *  appears in the SAME group of the loaded seed. */
export function liveKeySetForGroup(seed: PassDesign, group: FieldGroup): Set<string> {
  return new Set((seed.fields[group] ?? []).map((f) => f.key));
}

/**
 * Label write with the slug-on-first-label-edit rule — the panel's
 * PassFieldGroupEditor semantics, hoisted so FieldInspector/BackOfPass share
 * ONE source. A custom row is born `{ key: '', value: '' }` (addField), and a
 * surface exposing no key input would otherwise leave the key '' forever —
 * an unrepairable validatePassDesign 422 (autosave suppressed, deletion the
 * only way out). While the key is EMPTY, the first label edit slugs it
 * (deduped against the group's present keys AND the caller's reserved/live
 * keys, so a custom field can never collide into a live key — e.g. naming a
 * field "Gate" must not make it event-updated). A non-empty key is FROZEN:
 * label edits then go through plain setFieldProp (delete-on-empty preserved),
 * so a seeded/live row's stable key can never be re-slugged out from under
 * applyEventPatchToDesign.
 */
export function setFieldLabelSlugged(
  design: PassDesign,
  group: FieldGroup,
  index: number,
  label: string,
  reservedKeys: Iterable<string> = [],
): PassDesign {
  const rows = readGroup(design, group);
  const current = rows[index];
  if (current === undefined) return design;
  if (current.key !== '') {
    return setFieldProp(design, group, index, 'label', label);
  }
  const operatorKeys = rows.map((r) => r.key).filter((k) => k !== '');
  const base = slugifyKey(label);
  const key = dedupeKeyInGroup([...operatorKeys, ...reservedKeys], base);
  const nextRows = rows.map((r, i) => (i === index ? { ...r, label, key } : r));
  return setGroup(design, group, nextRows);
}
